// Stale-asset recovery (WF-050; quirk Q-GLB-30, PRESERVE "the rebuild must handle chunk-load errors with reload";
// Jarrett's plan-docs `backoffice-stale-asset-recovery`, Phase 1, recommended there and taken here). After a deploy,
// a tab still on the old build asks for a page's code that is no longer served, and Vite fires `vite:preloadError`.
// The back office reloads once, in place, to get the new build. A reload is counted per page in session storage, so
// a page whose code is really missing (a broken build, a blocked asset) never reloads in a loop: the second time the
// error is left to the page and a notice offers to try again. Matching error text instead would differ by browser and
// rot, and a status code can't tell (the static host answers a missing chunk with the app's index page).
import { useEffect, useState } from 'react';
import { Button } from '@radish/ui-web';

/** Where the reloads of this session are counted (the browser's session storage, or a stand-in in tests). */
export interface RecoveryStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
}

const KEY = 'radish.backoffice.chunk-reload:';

/** What to do when a page's code fails to load: reload once per page and session, else give up. */
export function chunkRecovery(path: string, store: RecoveryStore | null): 'reload' | 'give_up' {
  // With no session storage a reload can't be counted, and an uncounted reload may loop: give up instead.
  if (store === null) return 'give_up';
  try {
    if (store.get(KEY + path) !== null) return 'give_up';
    store.set(KEY + path, String(Date.now()));
    return 'reload';
  } catch {
    return 'give_up';
  }
}

/** The browser's session storage, or null where it is blocked (a private window, cleared site data). */
function sessionStore(w: Window): RecoveryStore | null {
  try {
    const s = w.sessionStorage;
    return { get: (k) => s.getItem(k), set: (k, v) => { s.setItem(k, v); } };
  } catch {
    return null;
  }
}

/**
 * Takes over Vite's preload error: the first failure for the page reloads it in place (`location.replace`, so no
 * history entry is added); a second is left to the page, and `onGiveUp` shows the notice. Returns the uninstaller.
 */
export function installStaleAssetRecovery(w: Window, store: () => RecoveryStore | null, onGiveUp: () => void): () => void {
  const onError = (e: Event) => {
    const path = w.location.pathname + w.location.search;
    if (chunkRecovery(path, store()) === 'reload') {
      e.preventDefault();
      w.location.replace(w.location.href);
      return;
    }
    onGiveUp();
  };
  w.addEventListener('vite:preloadError', onError);
  return () => w.removeEventListener('vite:preloadError', onError);
}

/** The notice for a page whose code still won't load after one reload. */
export function StaleAssetNotice({ shown, onReload }: { readonly shown: boolean; readonly onReload: () => void }) {
  if (!shown) return null;
  return (
    <div role="alert" className="fixed inset-x-0 top-0 z-50 flex items-center justify-center gap-3 bg-negative px-4 py-2 text-[14px] text-white">
      <span>Couldn&apos;t load this page. Check your connection, then try again.</span>
      <Button variant="outline" onClick={onReload}>Try again</Button>
    </div>
  );
}

/** Installs the recovery on the real window for the app's lifetime and shows the notice when it gives up. */
export function StaleAssetRecovery() {
  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => installStaleAssetRecovery(window, () => sessionStore(window), () => setGaveUp(true)), []);
  return <StaleAssetNotice shown={gaveUp} onReload={() => window.location.reload()} />;
}
